import { useState, useEffect, useRef } from 'react';
import BackButton from '../components/BackButton';
import { useApp } from '../App';
import api from '../api';
import Loading from '../components/Loading';
import { useI18n } from '../i18n/index.jsx';


export default function InvoicesIn() {
  const { user, isSuperuser, currentCompany } = useApp();
  const { t } = useI18n();
  const STATUSES = {
  pending: { label: t('invoices_in.status_pending'), color: '#f59e0b', icon: '⏳' },
  approved: { label: t('invoices_in.status_approved'), color: '#10b981', icon: '✅' },
  rejected: { label: t('invoices_in.status_rejected'), color: '#ef4444', icon: '❌' },
};
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [msg, setMsg] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => { loadInvoices(); }, [currentCompany]);

  async function loadInvoices() {
    try {
      const qs = currentCompany?.id ? `?company_id=${currentCompany.id}` : '';
      const res = await api.get(`/invoices/in${qs}`);
      setInvoices(res.data || []);
    } catch (e) {
      // Load from localStorage as fallback
      const saved = localStorage.getItem('invoices_in');
      if (saved) setInvoices(JSON.parse(saved));
    } finally {
      setLoading(false);
    }
  }

  async function handleFileSelect(e, isCamera = false) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check if it's an image
    if (file.type.startsWith('image/')) {
      await scanWithOCR(file);
    } else {
      // PDF or other - just upload
      await uploadFile(file);
    }
  }

  async function scanWithOCR(file) {
    setScanning(true);
    setMsg(t('invoices_in.scanning_invoice'));
    
    try {
      // Convert to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Send to backend for OCR
      const res = await api.post('/invoices/in/ocr', {
        image: base64,
        filename: file.name,
      });

      if (res.data?.parsed) {
        setScannedData({
          ...res.data.parsed,
          image: base64,
          originalFile: file,
        });
        setMsg('✅ ' + t('invoices_in.recognized'));
      } else {
        // Fallback - manual entry
        setScannedData({
          seller_name: '',
          seller_nip: '',
          invoice_number: '',
          issue_date: new Date().toISOString().split('T')[0],
          net_amount: 0,
          vat_amount: 0,
          gross_amount: 0,
          image: base64,
          originalFile: file,
        });
        setMsg('⚠️ ' + t('invoices_in.not_recognized'));
      }
    } catch (e) {
      console.error('OCR error:', e);
      // Allow manual entry
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      setScannedData({
        seller_name: '',
        seller_nip: '',
        invoice_number: '',
        issue_date: new Date().toISOString().split('T')[0],
        net_amount: 0,
        vat_amount: 0,
        gross_amount: 0,
        image: base64,
        originalFile: file,
      });
      setMsg('⚠️ ' + t('invoices_in.ocr_unavailable'));
    } finally {
      setScanning(false);
    }
  }

  async function uploadFile(file) {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post('/invoices/in/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMsg('✅ ' + t('invoices_in.uploaded'));
      loadInvoices();
    } catch (e) {
      setMsg('❌ ' + t('common.error') + ': ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  }

  async function saveScannedInvoice() {
    if (!scannedData) return;
    
    setUploading(true);
    try {
      const invoiceData = {
        seller_name: scannedData.seller_name,
        seller_nip: scannedData.seller_nip,
        invoice_number: scannedData.invoice_number,
        issue_date: scannedData.issue_date,
        net_amount: parseFloat(scannedData.net_amount) || 0,
        vat_amount: parseFloat(scannedData.vat_amount) || 0,
        gross_amount: parseFloat(scannedData.gross_amount) || 0,
        status: 'pending',
        image_data: scannedData.image,
      };

      try {
        await api.post('/invoices/in/save', invoiceData);
      } catch (e) {
        // Save locally as fallback
        const newInvoice = {
          id: Date.now(),
          ...invoiceData,
          created_at: new Date().toISOString(),
          user_id: user?.id,
        };
        const updated = [newInvoice, ...invoices];
        setInvoices(updated);
        localStorage.setItem('invoices_in', JSON.stringify(updated));
      }

      setMsg('✅ ' + t('invoices_in.sent_for_review'));
      setScannedData(null);
      loadInvoices();
    } catch (e) {
      setMsg('❌ ' + t('common.error') + ': ' + e.message);
    } finally {
      setUploading(false);
    }
  }

  function updateScannedField(field, value) {
    setScannedData(prev => ({ ...prev, [field]: value }));
  }

  if (loading) return <Loading />;

  // Scanned data form
  if (scannedData) {
    return (
      <div className="page">
        <BackButton />
        <h1>{t('invoices_in.check_data')}</h1>

        {/* Preview image */}
        {scannedData.image && (
          <div style={{
            background: '#fff',
            borderRadius: 8,
            padding: 8,
            marginBottom: 16,
            textAlign: 'center',
          }}>
            <img 
              src={scannedData.image} 
              alt="Invoice" 
              style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4 }}
            />
          </div>
        )}

        <div className="form">
          <div className="field">
            <label>{t('invoices_in.seller')}</label>
            <input
              value={scannedData.seller_name}
              onChange={e => updateScannedField('seller_name', e.target.value)}
              placeholder="Nazwa firmy"
            />
          </div>

          <div className="field">
            <label>{t('invoices_in.seller_nip')}</label>
            <input
              value={scannedData.seller_nip}
              onChange={e => updateScannedField('seller_nip', e.target.value)}
              placeholder="0000000000"
            />
          </div>

          <div className="field">
            <label>{t('invoices_in.invoice_number')}</label>
            <input
              value={scannedData.invoice_number}
              onChange={e => updateScannedField('invoice_number', e.target.value)}
              placeholder="FV/001/2026"
            />
          </div>

          <div className="field">
            <label>{t('invoices_in.issue_date')}</label>
            <input
              type="date"
              value={scannedData.issue_date}
              onChange={e => updateScannedField('issue_date', e.target.value)}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div className="field" style={{ flex: 1 }}>
              <label>Netto</label>
              <input
                type="number"
                value={scannedData.net_amount}
                onChange={e => updateScannedField('net_amount', e.target.value)}
              />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>VAT</label>
              <input
                type="number"
                value={scannedData.vat_amount}
                onChange={e => updateScannedField('vat_amount', e.target.value)}
              />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>Brutto</label>
              <input
                type="number"
                value={scannedData.gross_amount}
                onChange={e => updateScannedField('gross_amount', e.target.value)}
              />
            </div>
          </div>
        </div>

        {msg && <div className="message">{msg}</div>}

        <div className="actions">
          <button 
            className="btn btn-primary" 
            onClick={saveScannedInvoice}
            disabled={uploading || !scannedData.seller_name}
          >
            {uploading ? `⏳ ${t('invoices_in.saving')}` : `✅ ${t('invoices_in.save_send')}`}
          </button>
          <button className="btn btn-secondary" onClick={() => setScannedData(null)}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <BackButton />
      <h1>{t('invoices_in.title')}</h1>

      {/* Upload buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={(e) => handleFileSelect(e, true)}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        
        <button
          className="btn btn-primary"
          onClick={() => cameraInputRef.current?.click()}
          disabled={uploading || scanning}
          style={{ flex: 1 }}
        >
          {scanning ? '🔍 ' + t('invoices_in.scanning') : `📷 ${t('invoices_in.scan')}`}
        </button>
        
        <button
          className="btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || scanning}
          style={{ background: 'rgba(255,255,255,0.1)' }}
        >
          📁 {t('invoices_in.file')}
        </button>
      </div>

      {/* Info */}
      <div style={{
        background: 'rgba(99,102,241,0.1)',
        borderRadius: 8,
        padding: 12,
        marginBottom: 16,
        fontSize: 13,
      }}>
        {t('invoices_in.scan_info')}
      </div>

      {msg && <div className="message">{msg}</div>}

      {/* Invoices List */}
      {invoices.length === 0 ? (
        <p className="empty">{t('invoices_in.no_invoices')}</p>
      ) : (
        <div className="list">
          {invoices.map(inv => {
            const statusInfo = STATUSES[inv.status] || STATUSES.pending;
            return (
              <div key={inv.id} className="card">
                <div className="row">
                  <strong>{inv.invoice_number || t('invoices_in.no_number')}</strong>
                  <span style={{
                    background: statusInfo.color,
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: 11,
                  }}>
                    {statusInfo.icon} {statusInfo.label}
                  </span>
                </div>
                <div className="row secondary">
                  <span>{inv.seller_name || t('invoices_in.seller_not_specified')}</span>
                  <span style={{ fontWeight: 'bold', color: '#10b981' }}>
                    {inv.gross_amount?.toFixed(2)} PLN
                  </span>
                </div>
                <div className="row secondary">
                  <small>{inv.issue_date}</small>
                  <small>NIP: {inv.seller_nip || '—'}</small>
                </div>

                {inv.image_data && (
                  <div style={{ marginTop: 8 }}>
                    <img 
                      src={inv.image_data} 
                      alt="Invoice" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: 100, 
                        borderRadius: 4,
                        opacity: 0.8,
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
